import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { useCallback, useContext, useRef, useState } from 'react';
import { When } from 'react-if';
import { Button } from '../../../common/view/button/button/button';
import { Color } from '../../../common/view/color/color';
import { EditableText } from '../../../common/view/editable-text/editable-text';
import { EffectContext } from '../../../common/view/effect/effect.context';
import { MonoIcon } from '../../../common/view/icon/mono/mono-icon';
import { MenuPositions } from '../../../common/view/menu/menu-positions';
import { ImmerFuncs } from '../../../external/immer/immer.funcs';
import { NodeWire } from '../../../external/server/node.wire';
import { ServerApi } from '../../../external/server/server.api';
import { CssFuncs } from '../../../foundation/css/css.funcs';
import { RectFuncs } from '../../../foundation/geometry/rect.funcs';
import { ArrayFuncs } from '../../../foundation/js/array.funcs';
import { JsFuncs } from '../../../foundation/js/js.funcs';
import { StringFuncs } from '../../../foundation/js/string.funcs';
import { GapX } from '../../../foundation/layout/gap-x';
import { GapY } from '../../../foundation/layout/gap-y';
import { StackX } from '../../../foundation/layout/stack-x';
import { StackY } from '../../../foundation/layout/stack-y';
import { TripleX } from '../../../foundation/layout/triple-x';
import { BasicNodeSubWireFuncs } from '../../core/node/basic-node-sub.wire.funcs';
import { NodeStore } from '../../core/node/node.store';
import { NodeWireFuncs } from '../../core/node/node.wire.funcs';
import { AppStore } from '../app/app.store';
import { UnifiedEffectNodeItem } from '../effect/unified-effect-node-item';
import { NodeItemActionButton } from './node-item-action-button';
import { NodeItemMenu } from './node-item-menu';
import { NodeItemSelfStyle } from './node-item-self.style';

export function NodeItemSelf({
  nodeId,
  parentId,

  onFocusChanged,
  didCreate,
  onRemove,

  theIndenttationLevel,

  draggable,
  isDraggingOverChild,
}: {
  nodeId?: string;
  parentId?: string;

  onFocusChanged?: (focused: boolean) => void;
  didCreate?: (node: NodeWire) => void;
  onRemove?: (nodeId: string) => void;

  theIndenttationLevel: number;

  draggable: { listeners: SyntheticListenerMap | undefined };
  isDraggingOverChild: number;
}) {
  const node = NodeStore.useItem(nodeId);
  const [hasText, setHasText] = useState(StringFuncs.isNotEmpty(node?.text));
  const currentTextRef = useRef(node?.text);

  const { popupApi } = useContext(EffectContext);
  const moreButtonRef = useRef<HTMLDivElement>(null);

  /*
   * MARK - Text
   */

  const handleFocus = useCallback(() => {
    onFocusChanged?.(true);
  }, [onFocusChanged]);

  const handleBlur = useCallback(
    async (text: string) => {
      onFocusChanged?.(false);

      if (nodeId) {
        // TODO: why `node` is undefined here?
        if (NodeStore.getItem(nodeId)?.text === text) {
          return;
        }

        NodeStore.updateItem(nodeId, { text });
        ServerApi.updateNode(nodeId, { text });
      } else {
        // add new item
        if (StringFuncs.isEmpty(text)) {
          return;
        }

        // local change
        const newNode = NodeWireFuncs.init({
          text,
        });
        NodeStore.updateItem(newNode.id, newNode);
        const createdNode = await ServerApi.createNode(newNode);
        NodeStore.updateItem(createdNode.id, createdNode);
        didCreate?.(createdNode);
      }
    },
    [nodeId, node, onFocusChanged, didCreate],
  );

  const handleChange = useCallback((text: string) => {
    currentTextRef.current = text;
    setHasText(StringFuncs.isNotEmpty(text));
  }, []);

  /*
   * MARK - Custom Key Down
   */

  const handleCustomKeyDown = useCallback(async (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      const selection = window.getSelection();
      if (JsFuncs.not(selection?.rangeCount)) {
        e.currentTarget.blur();
        return;
      }

      if (selection?.type !== 'Caret') {
        e.currentTarget.blur();
        return;
      }

      const cursorIndex = selection.focusOffset;
      const textLength = currentTextRef.current?.length ?? 0;

      const selectionInfo = {
        atTheEnd: cursorIndex === textLength,
        atTheStart: cursorIndex === 0,
      };
      e.currentTarget.blur();

      if (selectionInfo.atTheEnd) {
        if (node?.sub?.length ?? 0 > 0) {
          return;
        }
        // new line after
        const newEmptyNode = NodeWireFuncs.init({
          text: '',
        });
        const createdEmptyNode = await ServerApi.createNode(newEmptyNode);
        NodeStore.updateItem(createdEmptyNode.id, createdEmptyNode);

        const parentNode = NodeStore.getItem(parentId!);

        const sub = ImmerFuncs.produce(ArrayFuncs.ensure(parentNode?.sub), (draft) => {
          BasicNodeSubWireFuncs.insertItemAfterItem(draft, { child: createdEmptyNode.id, after: nodeId! });
        });
        NodeStore.updateItem(parentId!, { sub });
        ServerApi.updateNode(parentId!, { sub });

        //
      } else if (selectionInfo.atTheStart) {
        // new line before

        const newEmptyNode = NodeWireFuncs.init({
          text: '',
        });
        const createdEmptyNode = await ServerApi.createNode(newEmptyNode);
        NodeStore.updateItem(createdEmptyNode.id, createdEmptyNode);

        const parentNode = NodeStore.getItem(parentId!);

        const sub = ImmerFuncs.produce(ArrayFuncs.ensure(parentNode?.sub), (draft) => {
          BasicNodeSubWireFuncs.insertItemBeforeItem(draft, { child: createdEmptyNode.id, before: nodeId! });
        });
        NodeStore.updateItem(parentId!, { sub });
        ServerApi.updateNode(parentId!, { sub });

        //
      } else {
        // lose focus
        e.currentTarget.blur();
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();

      const selection = window.getSelection();
      if (JsFuncs.not(selection?.rangeCount)) {
        e.currentTarget.blur();
        return;
      }

      if (selection?.type !== 'Caret') {
        e.currentTarget.blur();
        return;
      }

      const cursorIndex = selection.focusOffset;
      const textLength = currentTextRef.current?.length ?? 0;

      const selectionInfo = {
        atTheEnd: cursorIndex === textLength,
        atTheStart: cursorIndex === 0,
      };

      e.currentTarget.blur();

      if (selectionInfo.atTheStart) {
        if (!e.shiftKey) {
          // indent in
          const parentNode = NodeStore.getItem(parentId!);
          if (!parentNode) {
            return;
          }
          const prevItemId = BasicNodeSubWireFuncs.getPrevItem(parentNode.sub, { child: nodeId! });
          if (!prevItemId) {
            return;
          }

          const prevItem = NodeStore.getItem(prevItemId);
          if (!prevItem) {
            return;
          }

          const newParentSub = ImmerFuncs.produce(ArrayFuncs.ensure(parentNode.sub), (draft) => {
            BasicNodeSubWireFuncs.removeItem(draft, { child: nodeId! });
          });

          const newPrevSub = ImmerFuncs.produce(ArrayFuncs.ensure(prevItem.sub), (draft) => {
            BasicNodeSubWireFuncs.addItem(draft, { child: nodeId! });
          });

          NodeStore.updateItem(parentId!, { sub: newParentSub });
          NodeStore.updateItem(prevItemId, { sub: newPrevSub });

          await ServerApi.updateNode(parentId!, { sub: newParentSub });
          await ServerApi.updateNode(prevItemId, { sub: newPrevSub });
        }
      }
    } else if (e.key === 'Backspace') {
      const selection = window.getSelection();
      if (JsFuncs.not(selection?.rangeCount)) {
        return;
      }

      if (selection?.type !== 'Caret') {
        return;
      }

      const cursorIndex = selection.focusOffset;
      // leftMost + empty
      if (cursorIndex === 0 && (currentTextRef.current === '' || currentTextRef.current === null)) {
        if (!nodeId || !parentId) {
          return;
        }

        const parentNode = NodeStore.getItem(parentId);

        // Delete current node
        const sub = ImmerFuncs.produce(ArrayFuncs.ensure(parentNode?.sub), (draft) => {
          BasicNodeSubWireFuncs.removeItem(draft, { child: nodeId });
        });
        NodeStore.updateItem(parentId, { sub });
        ServerApi.updateNode(parentId, { sub });
      }
    }
  }, []);

  const isPlaceholder = !node;
  const effects = ArrayFuncs.ensure(node?.effects);

  return (
    <NodeItemSelfStyle.Container
      className={CssFuncs.className((names) => {
        if (isDraggingOverChild > 0) {
          names.push('is-dragging-over-child');
        }
      })}
    >
      <When condition={!isPlaceholder}>
        <NodeItemSelfStyle.Anchor>
          <NodeItemSelfStyle.LeftButton>
            <Button icon={MonoIcon.gripVertical} color={Color.white7} {...draggable.listeners} />
          </NodeItemSelfStyle.LeftButton>
        </NodeItemSelfStyle.Anchor>
      </When>

      <TripleX.Container $align="top" $gap="10px">
        <TripleX.Side>
          <StackX>
            <GapX $gap={`${theIndenttationLevel * 24}px`} />
            <NodeItemActionButton nodeId={nodeId!} />
          </StackX>
        </TripleX.Side>
        <TripleX.Rest style={{ position: 'relative' }}>
          <NodeItemSelfStyle.Text>
            <EditableText
              text={node?.text}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={handleChange}
              onCustomKeyDown={isPlaceholder ? undefined : handleCustomKeyDown}
            />
          </NodeItemSelfStyle.Text>
          <When condition={isPlaceholder && !hasText}>
            <NodeItemSelfStyle.Placeholder>{'New Task / Note / Link'}</NodeItemSelfStyle.Placeholder>
          </When>
        </TripleX.Rest>
      </TripleX.Container>
      <When condition={!isPlaceholder}>
        <NodeItemSelfStyle.Actions>
          <StackX $gap="2px">
            <NodeItemSelfStyle.ActionButton>
              <Button
                theRef={moreButtonRef}
                icon={MonoIcon.more}
                color={Color.white7}
                onClick={() => {
                  const triggerRect = RectFuncs.from(moreButtonRef.current?.getBoundingClientRect() ?? {});
                  popupApi.push({
                    component: <NodeItemMenu nodeId={nodeId!} onRemove={onRemove} />,
                    triggerRect,
                    positions: MenuPositions,
                  });
                }}
              />
            </NodeItemSelfStyle.ActionButton>
            <TodayButton nodeId={nodeId} />
          </StackX>
        </NodeItemSelfStyle.Actions>
      </When>

      <When condition={ArrayFuncs.isNotEmpty(effects)}>
        <GapY $gap="4px" />
        <StackY style={{ opacity: node?.done ? 0.6 : 1 }}>
          {effects.map((effect) => {
            return (
              <TripleX.Container key={effect.id} $align="top">
                <TripleX.Side>
                  <GapX $gap={`${theIndenttationLevel * 24 + 30}px`} />
                </TripleX.Side>
                <TripleX.Rest>
                  <UnifiedEffectNodeItem nodeId={nodeId!} effect={effect} />
                </TripleX.Rest>
              </TripleX.Container>
            );
          })}
        </StackY>
      </When>
    </NodeItemSelfStyle.Container>
  );
}

function TodayButton({ nodeId }: { nodeId?: string }) {
  const appStore = AppStore.use();
  const todayNode = NodeStore.useItem(appStore.todayNodeId);
  const sub = ArrayFuncs.ensure(todayNode?.sub);

  const isSelected = sub.includes(nodeId);

  const handleClick = useCallback(() => {
    if (!nodeId) {
      return;
    }

    if (isSelected) {
      BasicNodeSubWireFuncs.removeItem(sub, { child: nodeId });
    } else {
      BasicNodeSubWireFuncs.addItem(sub, { child: nodeId });
    }

    NodeStore.updateItem(appStore.todayNodeId!, { sub });
    ServerApi.updateNode(appStore.todayNodeId!, { sub });
  }, [nodeId, isSelected, todayNode]);

  if (!isSelected) {
    return <></>;
  }

  return (
    <NodeItemSelfStyle.ToggleButton
      className={CssFuncs.className((names) => {
        names.push(isSelected ? 'selected' : 'not-selected');
      })}
    >
      <Button icon={MonoIcon.today} onClick={handleClick} />
    </NodeItemSelfStyle.ToggleButton>
  );
}
